import { APPLICATION_STATUS } from "@/configs/constants/ApplicationStatus";
import { Application, Invitation, Shift } from "@/models/Job";

/**
 * The inbox of the Kandidater tab, split by what the admin can act on:
 *
 * - `pendingApplications` — applications awaiting an answer (Boka/Neka).
 * - `openInvitations` — sent invitations ("önskningar") without an answer.
 * - `acceptedApplications` — booked via application; kept visible since the
 *   whole-job unbook (Avboka) lives here.
 * - `archived` — declined/cancelled/expired history, collapsed by default.
 */
export interface CandidateInbox {
	pendingApplications: Application[];
	openInvitations: Invitation[];
	acceptedApplications: Application[];
	archived: { applications: Application[]; invitations: Invitation[] };
}

const newestFirst = (a: { metadata?: { created?: string | Date } }, b: typeof a) =>
	new Date(b.metadata?.created ?? 0).getTime() - new Date(a.metadata?.created ?? 0).getTime();

/** An invitation not yet answered — the model leaves `status` unset until it is. */
export function isOpenInvitation(invitation: Invitation): boolean {
	return !invitation.status || invitation.status === "OPEN";
}

export function splitInbox(
	applications?: Application[],
	invitations?: Invitation[]
): CandidateInbox {
	const pendingApplications: Application[] = [];
	const acceptedApplications: Application[] = [];
	const archivedApplications: Application[] = [];
	for (const application of applications ?? []) {
		if (application.status === APPLICATION_STATUS.PENDING_ANSWER)
			pendingApplications.push(application);
		else if (application.status === APPLICATION_STATUS.ACCEPTED)
			acceptedApplications.push(application);
		else archivedApplications.push(application);
	}

	const openInvitations: Invitation[] = [];
	const archivedInvitations: Invitation[] = [];
	for (const invitation of invitations ?? []) {
		if (isOpenInvitation(invitation)) openInvitations.push(invitation);
		else archivedInvitations.push(invitation);
	}

	pendingApplications.sort(newestFirst);
	acceptedApplications.sort(newestFirst);
	archivedApplications.sort(newestFirst);
	openInvitations.sort(newestFirst);
	archivedInvitations.sort(newestFirst);

	return {
		pendingApplications,
		openInvitations,
		acceptedApplications,
		archived: { applications: archivedApplications, invitations: archivedInvitations },
	};
}

/**
 * The number the Kandidater tab badge shows: items waiting for an admin
 * decision or a consultant answer — pending applications + open invitations.
 */
export function pendingInboxCount(
	applications?: Application[],
	invitations?: Invitation[]
): number {
	const pending = (applications ?? []).filter(
		(application) => application.status === APPLICATION_STATUS.PENDING_ANSWER
	).length;
	return pending + (invitations ?? []).filter(isOpenInvitation).length;
}

export interface ShiftTime {
	id: string;
	start: string | Date;
	end: string | Date;
}

/**
 * Resolves an application's snapshotted shift ids to real times from the job's
 * current shifts, dropping ids that no longer exist, sorted chronologically.
 * Pass `onlyUnmannedIds` for a pending application to reflect what accepting
 * will ACTUALLY book — a shift taken by someone else since the applicant
 * applied won't be re-taken by the slot-aware accept.
 */
export function resolveApplicationShifts(
	application: Pick<Application, "shifts">,
	shiftById: Map<string, ShiftTime>,
	onlyUnmannedIds?: Set<string>
): ShiftTime[] {
	return (application.shifts ?? [])
		.map((shift) => shiftById.get(shift.id))
		.filter((shift): shift is ShiftTime => {
			if (!shift) return false;
			return !onlyUnmannedIds || onlyUnmannedIds.has(shift.id);
		})
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

/**
 * The shifts a consultant holds an OPEN invitation on, chronologically —
 * invitations are stored per shift (`shift.invitations`), the inbox
 * invitation itself carries no shift list. Powers the invitation card's
 * shift chips, mirroring the booked/pending cards.
 */
export function invitedShiftTimes(shifts: Shift[] | undefined, consultantId: string): ShiftTime[] {
	return (shifts ?? [])
		.filter((shift) =>
			(shift.invitations ?? []).some(
				(invitation) => invitation.consultant.id === consultantId && isOpenInvitation(invitation)
			)
		)
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

/** Build an id → {start,end} lookup from the job's shifts. */
export function shiftTimeMap(shifts?: Shift[]): Map<string, ShiftTime> {
	const map = new Map<string, ShiftTime>();
	for (const shift of shifts ?? []) map.set(shift.id, shift);
	return map;
}

/** Strict time overlap — shifts merely touching (08-12 + 12-16) don't collide. */
export function shiftTimesOverlap(
	a: Pick<ShiftTime, "start" | "end">,
	b: Pick<ShiftTime, "start" | "end">
): boolean {
	return new Date(a.start) < new Date(b.end) && new Date(b.start) < new Date(a.end);
}

/**
 * The default selection of the direct-booking shift picker: a maximal
 * non-overlapping subset of the available shifts, earliest first — the same
 * greedy allocation the backend books when no explicit selection is sent
 * (time-overlapping "slots" hold capacity for several consultants, so one
 * consultant gets at most one per window).
 */
export function allocateBookableShifts(shifts: ShiftTime[]): ShiftTime[] {
	const sorted = [...shifts].sort(
		(a, b) => new Date(a.start).getTime() - new Date(b.start).getTime() || a.id.localeCompare(b.id)
	);

	const allocated: ShiftTime[] = [];
	for (const shift of sorted) {
		if (!allocated.some((taken) => shiftTimesOverlap(taken, shift))) allocated.push(shift);
	}
	return allocated;
}

/** Shift ids of the job that no consultant is booked on (no ACCEPTED application). */
export function unmannedShiftIds(shifts?: Shift[]): Set<string> {
	const ids = new Set<string>();
	for (const shift of shifts ?? []) {
		const booked = (shift.applications ?? []).some(
			(application) => application.status === APPLICATION_STATUS.ACCEPTED
		);
		if (!booked) ids.add(shift.id);
	}
	return ids;
}
